import React, { useState } from "react";
import PdfViewer from "./PdfViewer";

// Admin workflow: pick a field type + signer, then click a page to drop
// that field. Coordinates are stored normalised (0..1) so they stay valid
// when pdf-lib re-renders the PDF server-side.
export default function FieldPlacer({
  file,
  signers,
  fields,
  onAddField,
  onRemoveField,
  onResizeField,
  onMoveField
}) {
  const [pageSizes, setPageSizes] = useState({});
  const [activeSignerId, setActiveSignerId] = useState(signers[0]?.id ?? null);
  const [activeType, setActiveType] = useState("signature");

  const handlePageClick = (pageNumber) => (e) => {
    if (!activeSignerId) return;
    const size = pageSizes[pageNumber];
    if (!size) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const defaults =
      activeType === "date"
        ? { w: 0.14, h: 0.03, yOffset: 0.015 }
        : { w: 0.165, h: 0.052, yOffset: 0.026 };
    onAddField({
      signerId: activeSignerId,
      type: activeType,
      page: pageNumber,
      x: Math.max(0, Math.min(1, x - defaults.w / 2)),
      y: Math.max(0, Math.min(1, y - defaults.yOffset)),
      w: defaults.w,
      h: defaults.h
    });
  };

  const startResize = (idx, field) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    const handle = e.currentTarget;
    const fieldEl = handle.parentElement;
    const pageWidthPx = fieldEl.offsetWidth / field.w;
    const pageHeightPx = fieldEl.offsetHeight / field.h;
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = field.w;
    const startH = field.h;
    const move = (ev) => {
      const dxN = (ev.clientX - startX) / pageWidthPx;
      const dyN = (ev.clientY - startY) / pageHeightPx;
      const newW = Math.max(0.04, Math.min(1 - field.x, startW + dxN));
      const newH = Math.max(0.02, Math.min(1 - field.y, startH + dyN));
      onResizeField?.(idx, { w: newW, h: newH });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const startMove = (idx, field) => (e) => {
    // Only react to primary mouse button / primary touch. Secondary buttons
    // (right-click) and the × / resize-handle children call stopPropagation
    // themselves so they never reach here.
    if (e.button !== undefined && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const fieldEl = e.currentTarget;
    const pageEl = fieldEl.parentElement;
    const pageWidthPx = pageEl.offsetWidth;
    const pageHeightPx = pageEl.offsetHeight;
    const startX = e.clientX;
    const startY = e.clientY;
    const startFx = field.x;
    const startFy = field.y;
    // Capture the pointer so subsequent move/up fire on this element even
    // if the pointer slips outside its bounds mid-drag.
    try {
      fieldEl.setPointerCapture(e.pointerId);
    } catch {
      /* not all browsers expose pointer capture — listeners below still work */
    }
    const move = (ev) => {
      const dxN = (ev.clientX - startX) / pageWidthPx;
      const dyN = (ev.clientY - startY) / pageHeightPx;
      const newX = Math.max(0, Math.min(1 - field.w, startFx + dxN));
      const newY = Math.max(0, Math.min(1 - field.h, startFy + dyN));
      onMoveField?.(idx, { x: newX, y: newY });
    };
    const up = (ev) => {
      try {
        fieldEl.releasePointerCapture(ev.pointerId);
      } catch {
        /* see above */
      }
      fieldEl.removeEventListener("pointermove", move);
      fieldEl.removeEventListener("pointerup", up);
      fieldEl.removeEventListener("pointercancel", up);
    };
    fieldEl.addEventListener("pointermove", move);
    fieldEl.addEventListener("pointerup", up);
    fieldEl.addEventListener("pointercancel", up);
  };

  const overlay = (pageNumber) => (
    <div
      onClick={handlePageClick(pageNumber)}
      className="absolute inset-0 cursor-crosshair"
    >
      {fields.map((f, idx) => {
        if (f.page !== pageNumber) return null;
        const signer = signers.find((s) => s.id === f.signerId);
        const isDate = f.type === "date";
        return (
          <div
            key={idx}
            onPointerDown={startMove(idx, f)}
            onClick={(ev) => ev.stopPropagation()}
            title="Drag to move — bottom-right corner resizes"
            className={
              "group absolute touch-none border-2 text-xs hover:border-4 " +
              (isDate
                ? "border-amber-500 bg-amber-100/50 text-amber-800"
                : "border-brand-500 bg-brand-100/40 text-brand-700")
            }
            style={{
              left: `${f.x * 100}%`,
              top: `${f.y * 100}%`,
              width: `${f.w * 100}%`,
              height: `${f.h * 100}%`,
              cursor: "move"
            }}
          >
            <div className="flex items-center justify-between px-1 leading-tight">
              <span className="truncate">
                {isDate ? "Date" : "Sign"} · {signer?.name || "Signer"}
              </span>
              <button
                className="ml-1 hover:text-red-600"
                onClick={(ev) => {
                  ev.stopPropagation();
                  onRemoveField(idx);
                }}
                title="Remove field"
              >
                ×
              </button>
            </div>
            <div
              onPointerDown={startResize(idx, f)}
              onClick={(ev) => ev.stopPropagation()}
              title="Drag to resize"
              className={
                "absolute bottom-0 right-0 h-3 w-3 cursor-nwse-resize touch-none " +
                (isDate ? "bg-amber-500" : "bg-brand-500")
              }
              style={{ clipPath: "polygon(100% 0, 100% 100%, 0 100%)" }}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <div className="sticky top-0 z-20 -mx-5 mb-3 border-b border-slate-200 bg-white/95 px-5 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/80 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {signers.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSignerId(s.id)}
              className={
                "rounded-full px-3 py-1 text-sm " +
                (activeSignerId === s.id
                  ? "bg-brand-500 text-white"
                  : "bg-slate-100 text-slate-700")
              }
            >
              {s.name || "Signer"}
            </button>
          ))}
        </div>
        <div className="ml-auto inline-flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            onClick={() => setActiveType("signature")}
            className={
              "rounded-l-md border border-slate-300 px-3 py-1 text-sm " +
              (activeType === "signature"
                ? "bg-brand-500 text-white border-brand-500"
                : "bg-white text-slate-700 hover:bg-slate-50")
            }
          >
            Signature
          </button>
          <button
            type="button"
            onClick={() => setActiveType("date")}
            className={
              "-ml-px rounded-r-md border border-slate-300 px-3 py-1 text-sm " +
              (activeType === "date"
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-white text-slate-700 hover:bg-slate-50")
            }
          >
            Date
          </button>
        </div>
      </div>
      <p className="mb-2 text-sm text-slate-500">
        Pick a signer and field type, then tap a page to drop it.
      </p>
      <PdfViewer
        file={file}
        onPageSize={(p, size) =>
          setPageSizes((prev) => ({ ...prev, [p]: size }))
        }
        overlay={overlay}
      />
    </div>
  );
}
